import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, RefreshCw, Send, SkipForward, Trash2, Undo2 } from "lucide-react";
import { adminApi, formatError, imageUrl } from "@/lib/adminApi";

const BADGE = {
  generating: "bg-white/10 text-[#d6d3cd]",
  draft: "bg-white/10 text-[#f5f3ef]",
  approved: "bg-[#c8a97e] text-[#0a0b0d]",
  published: "bg-emerald-400/90 text-[#0a0b0d]",
  skipped: "bg-white/5 text-[#b4b2ad]",
  failed: "bg-red-400/90 text-[#0a0b0d]",
};

const Btn = ({ onClick, children, testid, primary, disabled }) => (
  <button data-testid={testid} disabled={disabled} onClick={onClick} className={`flex items-center gap-2 rounded-full px-4 py-2 text-[12px] uppercase tracking-[0.14em] transition-colors duration-300 disabled:opacity-50 ${primary ? "bg-[#c8a97e] text-[#0a0b0d] hover:bg-[#f5f3ef]" : "border border-white/20 text-[#d6d3cd] hover:border-white/50"}`}>
    {children}
  </button>
);

export const PostCard = ({ post, connected, onChange }) => {
  const [caption, setCaption] = useState(post.caption || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setCaption(post.caption || "");
  }, [post.caption]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (msg) toast.success(msg);
      onChange(res);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const locked = post.status === "published" || post.status === "generating";
  const dirty = caption !== (post.caption || "");
  const tags = Array.isArray(post.hashtags) ? post.hashtags : [];

  return (
    <article data-testid={`post-card-${post.id}`} className="grid grid-cols-1 gap-6 border border-white/10 bg-[#121418] p-5 md:grid-cols-[260px_1fr]">
      <div className="relative aspect-[4/5] overflow-hidden bg-[#0a0b0d]">
        {post.image_id && post.status !== "generating" ? (
          <img data-testid={`post-image-${post.id}`} src={imageUrl(post.image_id)} alt={`${post.brand} post`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-[#b4b2ad]">
            {post.status === "generating" ? <><Loader2 className="h-6 w-6 animate-spin text-[#c8a97e]" /> Creating image & caption…</> : "No image"}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-display text-3xl">{post.brand}</span>
          <span data-testid={`post-status-${post.id}`} className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] ${BADGE[post.status] || BADGE.draft}`}>{post.status}</span>
          <span className="font-mono text-xs text-[#b4b2ad]">Posts {post.date} · 9:00</span>
        </div>
        <textarea data-testid={`post-caption-${post.id}`} disabled={locked} rows={5} className="field resize-none border border-white/10 px-3 text-sm" value={caption} onChange={(e) => setCaption(e.target.value)} />
        <p className="text-xs text-[#c8a97e]">{tags.map((t) => `#${t}`).join(" ")}</p>
        {post.error && <p data-testid={`post-error-${post.id}`} className="text-xs text-red-400">{post.error}</p>}
        {!locked && (
          <div className="flex flex-wrap gap-2">
            {dirty && <Btn testid={`post-save-${post.id}`} disabled={busy} onClick={() => run(() => adminApi.update(post.id, { caption }), "Caption saved")}>Save caption</Btn>}
            {post.status !== "approved" && <Btn primary testid={`post-approve-${post.id}`} disabled={busy || !post.image_id} onClick={() => run(() => adminApi.update(post.id, { caption, status: "approved" }), "Approved for posting")}><Check className="h-4 w-4" /> Approve</Btn>}
            {post.status === "approved" && <Btn testid={`post-unapprove-${post.id}`} disabled={busy} onClick={() => run(() => adminApi.update(post.id, { status: "draft" }))}><Undo2 className="h-4 w-4" /> Back to draft</Btn>}
            {post.status === "approved" && connected && <Btn primary testid={`post-publish-${post.id}`} disabled={busy} onClick={() => run(() => adminApi.publishNow(post.id), "Publishing…")}><Send className="h-4 w-4" /> Publish now</Btn>}
            {post.status !== "skipped" && <Btn testid={`post-skip-${post.id}`} disabled={busy} onClick={() => run(() => adminApi.update(post.id, { status: "skipped" }), "Skipped")}><SkipForward className="h-4 w-4" /> Skip</Btn>}
            <Btn testid={`post-regenerate-${post.id}`} disabled={busy} onClick={() => run(() => adminApi.regenerate(post.id), "Creating a new image…")}><RefreshCw className="h-4 w-4" /> New image</Btn>
            <Btn testid={`post-delete-${post.id}`} disabled={busy} onClick={() => run(async () => { await adminApi.remove(post.id); return { ...post, status: "deleted" }; }, "Deleted")}><Trash2 className="h-4 w-4" /></Btn>
          </div>
        )}
      </div>
    </article>
  );
};
